import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AgentDetailModal = ({ agent, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newCommissionRate, setNewCommissionRate] = useState(agent?.commissionRate || 0);
  const [lockReason, setLockReason] = useState('');

  if (!isOpen || !agent) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePasswordChange = () => {
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (newPassword?.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    alert(`Password changed successfully for ${agent?.name}`);
    setShowPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleCommissionChange = () => {
    if (newCommissionRate < 0 || newCommissionRate > 10) {
      alert('Commission rate must be between 0% and 10%');
      return;
    }
    alert(`Commission rate updated to ${newCommissionRate}% for ${agent?.name}`);
    setShowCommissionModal(false);
  };

  const handleLockToggle = () => {
    if (!agent?.isLocked && !lockReason?.trim()) {
      alert('Please provide a reason for locking the account');
      return;
    }
    const action = agent?.isLocked ? 'unlocked' : 'locked';
    alert(`Agent account ${action} successfully`);
    setShowLockModal(false);
    setLockReason('');
    onClose();
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
    { id: 'transactions', label: 'Transactions', icon: 'ArrowLeftRight' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' },
    { id: 'activity', label: 'Activity Log', icon: 'Clock' }
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary">
                    {agent?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                  </span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{agent?.name}</h2>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-muted-foreground flex items-center space-x-1">
                      <Icon name="MapPin" size={14} />
                      <span>{agent?.location}</span>
                    </span>
                    <span className="text-sm text-muted-foreground">{agent?.id}</span>
                    {agent?.isLocked && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error">
                        <Icon name="Lock" size={12} className="mr-1" />
                        Locked
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center space-x-3 mt-4">
              <button
                onClick={() => setShowPasswordModal(true)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center space-x-2"
              >
                <Icon name="Key" size={16} />
                <span>Change Password</span>
              </button>
              <button
                onClick={() => setShowCommissionModal(true)}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 flex items-center space-x-2"
              >
                <Icon name="Percent" size={16} />
                <span>Change Commission</span>
              </button>
              <button
                onClick={() => setShowLockModal(true)}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  agent?.isLocked
                    ? 'bg-success text-success-foreground hover:bg-success/90'
                    : 'bg-error text-error-foreground hover:bg-error/90'
                }`}
              >
                <Icon name={agent?.isLocked ? 'Unlock' : 'Lock'} size={16} />
                <span>{agent?.isLocked ? 'Unlock Account' : 'Lock Account'}</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border px-6">
            <div className="flex space-x-6">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`py-4 flex items-center space-x-2 border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-1">
                        <Icon name="Mail" size={16} />
                        <span>Email</span>
                      </div>
                      <p className="text-foreground font-medium">{agent?.email}</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-1">
                        <Icon name="Phone" size={16} />
                        <span>Phone</span>
                      </div>
                      <p className="text-foreground font-medium">{agent?.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Account Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Status</div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        agent?.status === 'active' ? 'bg-success/10 text-success' :
                        agent?.status === 'suspended'? 'bg-error/10 text-error' : 'bg-muted text-muted-foreground'
                      }`}>
                        {agent?.status?.charAt(0)?.toUpperCase() + agent?.status?.slice(1)}
                      </span>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">KYC Status</div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        agent?.kycStatus === 'verified' ? 'bg-success/10 text-success' :
                        'bg-warning/10 text-warning'
                      }`}>
                        {agent?.kycStatus?.charAt(0)?.toUpperCase() + agent?.kycStatus?.slice(1)}
                      </span>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Commission Rate</div>
                      <p className="text-foreground font-medium text-lg">{agent?.commissionRate}%</p>
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Performance Metrics</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Total Volume</div>
                      <p className="text-foreground font-bold text-xl">{formatCurrency(agent?.volume)}</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Total Revenue</div>
                      <p className="text-foreground font-bold text-xl">{formatCurrency(agent?.revenue)}</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Transfers</div>
                      <p className="text-foreground font-bold text-xl">{agent?.transfers}</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Customers</div>
                      <p className="text-foreground font-bold text-xl">{agent?.customers}</p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Timeline</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 rounded-full bg-success/10 flex items-center justify-center">
                        <Icon name="UserPlus" size={16} className="text-success" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">Joined Platform</p>
                        <p className="text-xs text-muted-foreground">{formatDate(agent?.joinedDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="Activity" size={16} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">Last Active</p>
                        <p className="text-xs text-muted-foreground">{formatDate(agent?.lastActive)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'performance' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-6 bg-muted/30 rounded-lg">
                    <h4 className="text-sm font-semibold text-foreground mb-4">Compliance Score</h4>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="text-4xl font-bold text-foreground">{agent?.complianceScore}%</div>
                        <div className="w-full bg-muted rounded-full h-2 mt-2">
                          <div
                            className={`h-2 rounded-full ${
                              agent?.complianceScore >= 95 ? 'bg-success' :
                              agent?.complianceScore >= 85 ? 'bg-warning' : 'bg-error'
                            }`}
                            style={{ width: `${agent?.complianceScore}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-muted/30 rounded-lg">
                    <h4 className="text-sm font-semibold text-foreground mb-4">Average Ticket Size</h4>
                    <div className="text-4xl font-bold text-foreground">
                      {formatCurrency(agent?.volume / agent?.transfers)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div>
                <p className="text-muted-foreground text-center py-8">
                  Transaction history would be displayed here
                </p>
              </div>
            )}

            {activeTab === 'compliance' && (
              <div>
                <p className="text-muted-foreground text-center py-8">
                  Compliance details would be displayed here
                </p>
              </div>
            )}

            {activeTab === 'activity' && (
              <div>
                <p className="text-muted-foreground text-center py-8">
                  Activity log would be displayed here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePasswordChange}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Change Password
                </button>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Commission Change Modal */}
      {showCommissionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Change Commission Rate</h3>
              <button
                onClick={() => setShowCommissionModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Current Rate: {agent?.commissionRate}%
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={newCommissionRate}
                    onChange={(e) => setNewCommissionRate(parseFloat(e?.target?.value))}
                    className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    placeholder="Enter new rate"
                    min="0"
                    max="10"
                    step="0.1"
                  />
                  <span className="text-foreground font-medium">%</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCommissionChange}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Update Rate
                </button>
                <button
                  onClick={() => setShowCommissionModal(false)}
                  className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lock/Unlock Modal */}
      {showLockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {agent?.isLocked ? 'Unlock Account' : 'Lock Account'}
              </h3>
              <button
                onClick={() => setShowLockModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              {!agent?.isLocked && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Reason for Locking <span className="text-error">*</span>
                  </label>
                  <textarea
                    value={lockReason}
                    onChange={(e) => setLockReason(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    placeholder="Provide reason for account lock"
                    rows={4}
                  />
                </div>
              )}
              <div className={`p-4 rounded-lg ${agent?.isLocked ? 'bg-success/10' : 'bg-error/10'}`}>
                <p className={`text-sm ${agent?.isLocked ? 'text-success' : 'text-error'}`}>
                  {agent?.isLocked
                    ? 'Unlocking this account will restore full access for the agent.'
                    : 'Locking this account will prevent the agent from accessing the system.'}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleLockToggle}
                  className={`flex-1 px-4 py-2 rounded-lg ${
                    agent?.isLocked
                      ? 'bg-success text-success-foreground hover:bg-success/90'
                      : 'bg-error text-error-foreground hover:bg-error/90'
                  }`}
                >
                  {agent?.isLocked ? 'Unlock Account' : 'Lock Account'}
                </button>
                <button
                  onClick={() => setShowLockModal(false)}
                  className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentDetailModal;